import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, Upload, CheckSquare, Cpu, ShieldCheck, FileText, BarChart3, History, LogOut, Bell, Menu, X, Landmark, List, FilePlus } from 'lucide-react';
export const Layout: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const {
    user,
    logout
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const navItems = [{
    path: '/',
    label: 'ড্যাশবোর্ড',
    icon: LayoutDashboard,
    roles: ['operator', 'officer', 'zso', 'admin', 'dsminland']
  }, {
    path: '/upload',
    label: 'ডাটা সাবমিশন',
    icon: Upload,
    roles: ['operator']
  }, {
    path: '/projnapan-list',
    label: 'গেজেট তালিকা',
    icon: FileText,
    roles: ['operator']
  }, {
    path: '/submissions',
    label: 'সকল সাবমিশন',
    icon: List,
    roles: ['operator', 'officer', 'admin']
  }, {
    path: '/validate',
    label: 'যাচাইকরণ',
    icon: CheckSquare,
    roles: ['officer']
  }, {
    path: '/processing',
    label: 'প্রক্রিয়াকরণ স্ট্যাটাস',
    icon: Cpu,
    roles: ['officer', 'admin']
  }, {
    path: '/approve',
    label: 'যাচাই ও অনুমোদন',
    icon: ShieldCheck,
    roles: ['admin']
  }, {
    path: '/viewer',
    label: 'খতিয়ান ভিউয়ার',
    icon: FileText,
    roles: ['operator', 'officer', 'admin']
  }, {
    path: '/reports',
    label: 'রিপোর্টস',
    icon: BarChart3,
    roles: ['officer', 'admin']
  }, {
    path: '/audit',
    label: 'অডিট ট্রেইল',
    icon: History,
    roles: ['admin']
  },
  // ZSO Gadget Routes
  {
    path: '/gadget/new',
    label: 'নতুন গেজেট প্রস্তুতি',
    icon: FilePlus,
    roles: ['zso']
  }, {
    path: '/gadget/submissions',
    label: 'গেজেট জমাদান তালিকা',
    icon: List,
    roles: ['zso']
  },
  // DS MinLand Gadget Routes
  {
    path: '/gadget/ds-submissions',
    label: 'গেজেট জমাদান তালিকা',
    icon: List,
    roles: ['dsminland']
  }, {
    path: '/gadget/approve',
    label: 'গেজেট অনুমোদন',
    icon: ShieldCheck,
    roles: ['dsminland']
  }, {
    path: '/gadget/projnapan',
    label: 'গেজেট প্রিভিউ',
    icon: FileText,
    roles: ['dsminland']
  },
  // Shared Gadget Routes
  {
    path: '/gadget/reports',
    label: 'রিপোর্টস',
    icon: BarChart3,
    roles: ['zso', 'dsminland']
  }];
  const filteredNavItems = navItems.filter(item => user && item.roles.includes(user.role));
  return <div className="min-h-screen bg-slate-50 flex" data-id="element-171">
      {/* Sidebar */}
      <aside className={`${isSidebarOpen ? 'w-64' : 'w-20'} bg-slate-900 text-white transition-all duration-300 flex flex-col fixed h-full z-20`} data-id="element-172">
        <div className="h-16 flex items-center justify-center border-b border-slate-800 px-4" data-id="element-173">
          <Landmark className="text-teal-500 min-w-[24px]" size={24} data-id="element-174" />
          {isSidebarOpen && <div className="ml-3 font-semibold text-sm leading-tight" data-id="element-175">
              জরিপ খতিয়ান
              <br data-id="element-176" />
              <span className="text-teal-400 font-normal text-xs" data-id="element-177">
                প্রকাশনা (ডিএলআরএস)
              </span>
            </div>}
        </div>

        <div className="flex-1 overflow-y-auto py-4" data-id="element-178">
          <nav className="space-y-1 px-2" data-id="element-179">
            {filteredNavItems.map(item => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return <Link key={item.path} to={item.path} className={`flex items-center px-3 py-2.5 rounded-md transition-colors ${isActive ? 'bg-teal-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`} title={!isSidebarOpen ? item.label : undefined} data-id="element-180">
                  <Icon size={20} className="min-w-[20px]" data-id="element-181" />
                  {isSidebarOpen && <span className="ml-3 text-sm font-medium" data-id="element-182">
                      {item.label}
                    </span>}
                </Link>;
          })}
          </nav>
        </div>

        <div className="p-4 border-t border-slate-800" data-id="element-183">
          <div className={`flex items-center ${isSidebarOpen ? 'justify-between' : 'justify-center'}`} data-id="element-184">
            {isSidebarOpen && <div className="flex flex-col overflow-hidden" data-id="element-185">
                <span className="text-sm font-medium truncate" data-id="element-186">
                  {user?.name}
                </span>
                <span className="text-xs text-slate-400 truncate" data-id="element-187">
                  {user?.designation}
                </span>
              </div>}
            <button onClick={handleLogout} className="text-slate-400 hover:text-white transition-colors p-1 rounded-md hover:bg-slate-800" title="লগআউট" data-id="element-188">
              <LogOut size={18} data-id="element-189" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`} data-id="element-190">
        {/* Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sticky top-0 z-10" data-id="element-191">
          <div className="flex items-center" data-id="element-192">
            <button onClick={() => setSidebarOpen(!isSidebarOpen)} className="p-2 rounded-md text-slate-500 hover:bg-slate-100 focus:outline-none" data-id="element-193">
              {isSidebarOpen ? <X size={20} data-id="element-194" /> : <Menu size={20} data-id="element-195" />}
            </button>
            <div className="ml-4 text-sm font-medium text-slate-800" data-id="element-196">
              {filteredNavItems.find(item => item.path === location.pathname)?.label || 'ড্যাশবোর্ড'}
            </div>
          </div>

          <div className="flex items-center space-x-4" data-id="element-197">
            <button className="relative p-2 text-slate-500 hover:bg-slate-100 rounded-full transition-colors" data-id="element-198">
              <Bell size={20} data-id="element-199" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border border-white" data-id="element-200"></span>
            </button>
            <div className="h-8 w-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-bold text-sm border border-teal-200" data-id="element-201">
              {user?.name.charAt(0)}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto" data-id="element-202">{children}</main>
      </div>
    </div>;
};